import { SVGProps } from "react";
import ComfortableHeadsetIcon from "@/components/icons/comfortable-headset-icon";
import EngagingLearningDesignIcon from "@/components/icons/engaging-learning-design-icon";
import ImmersiveExperience360Icon from "@/components/icons/immersive-experience-360-icon";
import ProfessionallyDesignedScenariosIcon from "@/components/icons/professionally-designed-scenarios-icon";
import ResolutionIcon from "@/components/icons/resolution-icon";
import WirelessPcVrTrainingIcon from "@/components/icons/wireless-pc-vr-training-icon";
import ResourcesTextCard from "@/components/ui/resources-text-card";
import { IconName } from "@/lib/get-icon";

type Feature = {
  title: string;
  ariaLabel: string;
  iconName?: IconName;
  icon?: React.FC<SVGProps<SVGSVGElement>>;
};

export interface FeaturesCIVRProps {
  id?: string;
  title?: string;
  features?: Feature[];
}

// Icon name to component mapping
const iconMap: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  "immersive-experience-360": ImmersiveExperience360Icon,
  resolution: ResolutionIcon,
  "professionally-designed-scenarios": ProfessionallyDesignedScenariosIcon,
  "engaging-learning-design": EngagingLearningDesignIcon,
  "wireless-pc-vr-training": WirelessPcVrTrainingIcon,
  "comfortable-headset": ComfortableHeadsetIcon,
};

// Helper to get icon component from either iconName or icon prop
const getIconComponent = (
  feature: Feature
): React.FC<React.SVGProps<SVGSVGElement>> => {
  if (feature.iconName && iconMap[feature.iconName]) {
    return iconMap[feature.iconName];
  }
  return feature.icon!;
};

const defaultFeatures: Feature[] = [
  {
    title: "360° Immersive Environment",
    iconName: "immersive-experience-360" as IconName,
    ariaLabel: "Feature: 360° Immersive Environment",
  },
  {
    title: "4K True RGB Display",
    iconName: "resolution" as IconName,
    ariaLabel: "Feature: 4K True RGB Display",
  },
  {
    title: "Professionally Designed Scenarios",
    iconName: "professionally-designed-scenarios" as IconName,
    ariaLabel: "Feature: Professionally Designed Scenarios",
  },
  {
    title: "Engaging Learning Design",
    iconName: "engaging-learning-design" as IconName,
    ariaLabel: "Feature: Engaging Learning Design",
  },
  {
    title: "Wireless PC VR streaming",
    iconName: "wireless-pc-vr-training" as IconName,
    ariaLabel: "Feature: Wireless PC VR streaming",
  },
  {
    title: "Extremely Comfortable Headset",
    iconName: "comfortable-headset" as IconName,
    ariaLabel: "Feature: Extremely Comfortable Headset",
  },
];

const FeaturesCIVR: React.FC<FeaturesCIVRProps> = ({
  id = "features",
  title = "Features",
  features = defaultFeatures,
}) => {
  return (
    <section
      id={id}
      aria-labelledby="features-heading"
      className="bg-primary-100"
    >
      <div className="mx-auto grid max-w-lg grid-cols-1 gap-4 p-4 md:gap-6 md:px-6 md:py-8 lg:gap-8 lg:px-3 lg:py-10">
        <h2
          id="features-heading"
          className="font-montserrat text-heading-4 text-primary-600 md:text-heading-3 lg:text-heading-2"
        >
          {title}
        </h2>
        <div
          className="grid grid-cols-2 gap-x-3 gap-y-4 md:gap-x-10 lg:grid-cols-3"
          role="list"
        >
          {features?.map((feature) => (
            <ResourcesTextCard
              cardType="icon-feature-vr"
              title={feature.title}
              icon={getIconComponent(feature)}
              aria-label={feature.ariaLabel}
              key={feature.title + " - " + feature.ariaLabel}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesCIVR;
